import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchApi } from '../services/api';

interface ApiTableResponse {
  table1?: CompanyInfo[];
  table?: UserInfo[];
}

interface CompanyInfo {
  업체번호: string;
  업체명: string;
  대표자: string;
  사업자등록번호: string;
  주소: string;
  전화번호: string;
  이메일: string;
  기본주소: string;
  상세주소: string;
  회사로고: string;
}

interface UserInfo {
  업체번호: string;
  유저번호: string;
  업체명: string;
  유저명: string;
  대표이메일: string;
  프로필사진: string;
}

interface UserContextType {
  userInfo: UserInfo | null;
  companyInfo: CompanyInfo | null;
  setUserInfo: (info: UserInfo | null) => void;
  setCompanyInfo: (info: CompanyInfo | null) => void;
  fetchUserInfo: () => Promise<void>;
  fetchCompanyInfo: () => Promise<void>;
  clearUserInfo: () => void;
  clearCompanyInfo: () => void;
  isLoading: boolean;
}

const UserContext = createContext<UserContextType>({
  userInfo: null,
  companyInfo: null,
  setUserInfo: () => {},
  setCompanyInfo: () => {},
  fetchUserInfo: async () => {},
  fetchCompanyInfo: async () => {},
  clearUserInfo: () => {},
  clearCompanyInfo: () => {},
  isLoading: false,
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const clearUserInfo = () => {
    setUserInfo(null);
  };

  const clearCompanyInfo = () => {
    setCompanyInfo(null);
  };

  const fetchCompanyInfo = async () => {
    const CompanyNo = localStorage.getItem('CompanyNo');
    if (!CompanyNo) {
      throw new Error('Company ID not found');
    }
    try {
      const postData = [{ "컬럼명": '@업체번호', "값": CompanyNo }];
      const response = await fetchApi<ApiTableResponse>('DBO.RCT_S_회사기본정보', postData);
      if (response?.data?.table1?.[0]) {
        setCompanyInfo(response.data.table1[0]);
      }
    } catch (err) {
      console.error('회사 정보를 불러오는데 실패했습니다.', err);
      throw err;
    }
  };

  const fetchUserInfo = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const CompanyNo = localStorage.getItem('CompanyNo');
      const UserNo = localStorage.getItem('UserNo');
      const procedureName = "DBO.CW_S_유저정보";
      const postData = [
        { "컬럼명": "@업체번호", "값": CompanyNo },
        { "컬럼명": "@유저번호", "값": UserNo }
      ];

      const response = await fetchApi<ApiTableResponse>(procedureName, postData);
      
      if (response.data && response.data.table) {
        setUserInfo(response.data.table[0]);
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 로그인 상태일 때만 사용자 정보를 가져옵니다
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (isLoggedIn) {
      fetchUserInfo();
      fetchCompanyInfo();
    }
  }, []);

  return (
    <UserContext.Provider
      value={{
        userInfo,
        companyInfo,
        setUserInfo,
        setCompanyInfo,
        fetchUserInfo,
        fetchCompanyInfo,
        clearUserInfo,
        clearCompanyInfo,
        isLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
